import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Injector,
  NgZone,
  PLATFORM_ID,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
  viewChildren,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { firstValueFrom } from 'rxjs';

import { PopupReusable } from '../popup-reusable/popup-reusable';

/**
 * What the viewer was handed. A signed URL is the normal case; a Blob is for
 * files the caller already has in memory (a freshly picked upload, say).
 */
export type DocumentViewerSource = string | Blob | null;

type RenderKind = 'pdf' | 'image' | 'unsupported';
type ViewMode = 'loading' | 'unsupported' | 'error' | 'image' | 'pdf' | 'pdf-frame';
type PdfState = 'idle' | 'loading' | 'ready' | 'failed';

/** Page size in CSS px at 100% zoom, normalised so every page is A4 wide. */
interface PdfPageSize {
  index: number;
  width: number;
  height: number;
  /** PDF points → CSS px at 100% zoom. */
  baseScale: number;
}

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'avif'];

/** A4 (210 × 297 mm) at 96 dpi — what "100%" means in this viewer. */
const A4_WIDTH_PX = 794;
const A4_HEIGHT_PX = 1123;

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 3;
const ZOOM_STEPS = [0.25, 0.5, 0.67, 0.75, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3];
/** Matches the p-6 around the sheets, so "fit" leaves the same gutter both sides. */
const SHEET_GUTTER_PX = 48;
/** iOS refuses canvases over ~16.7M px; stay under it at high zoom × DPR. */
const MAX_CANVAS_PIXELS = 16_000_000;

/**
 * View-only document preview.
 *
 * The whole point of this component is that the file is shown and nothing else:
 * there is no download button, PDFs are drawn onto canvases by pdf.js (so there
 * is no browser PDF toolbar at all), and save/print/right-click are swallowed
 * while it is open.
 *
 * Pages are laid out as A4 sheets — every PDF page is scaled to A4 width at
 * 100% — with zoom from 25% to 300% via the toolbar, Ctrl/Cmd + wheel (or a
 * trackpad pinch) and Ctrl/Cmd + `+` / `-` / `0`.
 *
 * That is a deterrent, not enforcement — the bytes still reach the browser, so
 * anyone with devtools can keep them. Real enforcement has to happen server-side.
 */
@Component({
  selector: 'app-document-viewer',
  standalone: true,
  imports: [CommonModule, PopupReusable],
  templateUrl: './document-viewer.html',
  styles: [
    `
      :host {
        display: contents;
      }

      /* Nothing inside the frame is selectable or draggable, so the image can't
         be dragged out to the desktop and text can't be copied wholesale. */
      .viewer-stage,
      .viewer-stage * {
        -webkit-user-select: none;
        user-select: none;
        -webkit-user-drag: none;
      }

      .viewer-sheet {
        box-shadow:
          0 1px 3px rgb(15 23 42 / 0.12),
          0 4px 12px rgb(15 23 42 / 0.08);
      }
    `,
  ],
})
export class DocumentViewer {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);
  private readonly http = inject(HttpClient);
  private readonly zone = inject(NgZone);
  private readonly injector = inject(Injector);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly isOpen = input(false);
  /** Signed URL or Blob. Null while the caller is still resolving it. */
  readonly source = input<DocumentViewerSource>(null);
  readonly fileName = input('Document');
  /** Set while the caller fetches the signed URL, so the modal opens on click. */
  readonly loading = input(false);
  /** Overrides extension sniffing when the API tells us the type. */
  readonly mimeType = input<string | null>(null);

  readonly closed = output<void>();

  private readonly stageRef = viewChild<ElementRef<HTMLElement>>('stage');
  private readonly scrollerRef = viewChild<ElementRef<HTMLElement>>('scroller');
  private readonly pageRefs = viewChildren<ElementRef<HTMLElement>>('pageEl');

  protected readonly resolving = signal(false);
  protected readonly failed = signal(false);
  protected readonly objectUrl = signal<string | null>(null);
  /** Set when the blob fetch was blocked by CORS and we fell back to the raw URL. */
  protected readonly directUrl = signal<string | null>(null);

  protected readonly pdfState = signal<PdfState>('idle');
  protected readonly pages = signal<PdfPageSize[]>([]);
  protected readonly currentPage = signal(1);
  /** 1 = A4 width at 96 dpi. */
  protected readonly zoom = signal(1);

  protected readonly a4Width = A4_WIDTH_PX;
  protected readonly a4Height = A4_HEIGHT_PX;

  private revokeHandle: string | null = null;
  private pdfDoc: PDFDocumentProxy | null = null;
  /** Bumped on every release so late async work from a previous file is dropped. */
  private loadToken = 0;
  private readonly renderTasks = new Map<number, RenderTask>();
  /** Effective scale each page's canvas was last drawn at. */
  private readonly renderedAt = new Map<number, number>();
  private readonly visiblePages = new Set<number>();
  private pageObserver: IntersectionObserver | null = null;
  private renderTimer: ReturnType<typeof setTimeout> | null = null;
  private detachScroller: (() => void) | null = null;

  protected readonly busy = computed(() => this.loading() || this.resolving());

  protected readonly kind = computed<RenderKind>(() => {
    const explicit = this.mimeType()?.toLowerCase() ?? '';
    if (explicit.includes('pdf')) return 'pdf';
    if (explicit.startsWith('image/')) return 'image';

    const extension = this.extensionOf(this.fileName());
    if (extension === 'pdf') return 'pdf';
    if (IMAGE_EXTENSIONS.includes(extension)) return 'image';
    return 'unsupported';
  });

  /** The resolved URL, whether it came from a blob or the signed URL fallback. */
  protected readonly resolvedUrl = computed(() => this.objectUrl() ?? this.directUrl());

  protected readonly viewMode = computed<ViewMode>(() => {
    if (this.busy()) return 'loading';
    if (this.kind() === 'unsupported') return 'unsupported';
    if (!this.resolvedUrl()) return 'error';
    if (this.kind() === 'image') return 'image';

    switch (this.pdfState()) {
      case 'loading':
        return 'loading';
      case 'ready':
        return 'pdf';
      default:
        // pdf.js couldn't take it (CORS fallback URL, or a file it can't parse).
        return 'pdf-frame';
    }
  });

  protected readonly canZoom = computed(() => {
    const mode = this.viewMode();
    return mode === 'pdf' || mode === 'image';
  });

  protected readonly zoomPercent = computed(() => Math.round(this.zoom() * 100));
  protected readonly canZoomIn = computed(() => this.zoom() < ZOOM_MAX - 0.001);
  protected readonly canZoomOut = computed(() => this.zoom() > ZOOM_MIN + 0.001);

  /**
   * Only used by the iframe fallback. The hash params strip Chrome's built-in
   * PDF toolbar — which is where its download and print buttons live. Firefox
   * ignores them.
   */
  protected readonly frameUrl = computed<SafeResourceUrl | null>(() => {
    const url = this.resolvedUrl();
    if (!url) return null;

    const suffix = this.kind() === 'pdf' ? '#toolbar=0&navpanes=0&scrollbar=0&view=FitH' : '';
    return this.sanitizer.bypassSecurityTrustResourceUrl(`${url}${suffix}`);
  });

  constructor() {
    effect(() => {
      const source = this.source();
      const open = this.isOpen();

      untracked(() => {
        if (!open) {
          this.release();
          return;
        }

        if (source) {
          void this.resolve(source);
        }
      });
    });

    // Re-observe whenever the set of page sheets changes (new file loaded).
    effect(() => {
      const elements = this.pageRefs().map((ref) => ref.nativeElement);
      const scroller = this.scrollerRef()?.nativeElement ?? null;
      untracked(() => this.observePages(elements, scroller));
    });

    effect(() => {
      const scroller = this.scrollerRef()?.nativeElement ?? null;
      untracked(() => this.attachScroller(scroller));
    });

    // Canvases stretch with CSS straight away; redraw them sharp once zooming settles.
    effect(() => {
      this.zoom();
      untracked(() => this.scheduleRender());
    });

    this.destroyRef.onDestroy(() => {
      this.release();
      this.attachScroller(null);
    });
  }

  /** Ctrl/Cmd+S and Ctrl/Cmd+P are the two one-keystroke ways out of a preview. */
  @HostListener('document:keydown', ['$event'])
  protected onKeydown(event: KeyboardEvent): void {
    if (!this.isOpen()) {
      return;
    }

    if (event.key === 'Escape') {
      this.onClose();
      return;
    }

    if (!(event.ctrlKey || event.metaKey)) {
      return;
    }

    const key = event.key.toLowerCase();
    if (key === 's' || key === 'p') {
      event.preventDefault();
      return;
    }

    // While the viewer is open, browser zoom shortcuts zoom the document instead.
    if (!this.canZoom()) {
      return;
    }

    if (key === '+' || key === '=') {
      event.preventDefault();
      this.zoomIn();
    } else if (key === '-' || key === '_') {
      event.preventDefault();
      this.zoomOut();
    } else if (key === '0') {
      event.preventDefault();
      this.setZoom(1);
    }
  }

  protected onContextMenu(event: MouseEvent): void {
    event.preventDefault();
  }

  protected onClose(): void {
    this.release();
    this.closed.emit();
  }

  protected zoomIn(): void {
    const current = this.zoom();
    const next = ZOOM_STEPS.find((step) => step > current + 0.001) ?? ZOOM_MAX;
    this.setZoom(next);
  }

  protected zoomOut(): void {
    const current = this.zoom();
    const next = [...ZOOM_STEPS].reverse().find((step) => step < current - 0.001) ?? ZOOM_MIN;
    this.setZoom(next);
  }

  protected resetZoom(): void {
    this.setZoom(1);
  }

  protected fitToWidth(): void {
    this.setZoom(this.fitZoom());
  }

  /**
   * Changes zoom while keeping the point at the centre of the viewport where
   * it was, so zooming doesn't throw the reader back to page 1.
   */
  private setZoom(value: number): void {
    const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(value * 100) / 100));
    if (Math.abs(next - this.zoom()) < 0.001) {
      return;
    }

    const scroller = this.scrollerRef()?.nativeElement;
    const anchor = scroller
      ? {
          x: (scroller.scrollLeft + scroller.clientWidth / 2) / Math.max(scroller.scrollWidth, 1),
          y: (scroller.scrollTop + scroller.clientHeight / 2) / Math.max(scroller.scrollHeight, 1),
        }
      : null;

    this.zoom.set(next);

    if (scroller && anchor) {
      afterNextRender(
        () => {
          scroller.scrollLeft = anchor.x * scroller.scrollWidth - scroller.clientWidth / 2;
          scroller.scrollTop = anchor.y * scroller.scrollHeight - scroller.clientHeight / 2;
        },
        { injector: this.injector },
      );
    }
  }

  /** Zoom at which an A4 sheet exactly fills the visible width. */
  private fitZoom(): number {
    const host = this.scrollerRef()?.nativeElement ?? this.stageRef()?.nativeElement;
    const available = (host?.clientWidth ?? A4_WIDTH_PX + SHEET_GUTTER_PX) - SHEET_GUTTER_PX;
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, available / A4_WIDTH_PX));
  }

  /** Opens at true A4 size (100%) unless the window is too narrow, then fits. */
  private applyDefaultZoom(): void {
    afterNextRender(
      () => {
        this.zoom.set(Math.min(1, Math.floor(this.fitZoom() * 100) / 100));
        this.scrollerRef()?.nativeElement.scrollTo({ top: 0, left: 0 });
      },
      { injector: this.injector },
    );
  }

  /**
   * Fetching the file as a blob keeps the signed URL out of the DOM, so it can't
   * be lifted from the iframe's src and pasted into a new tab. When CORS blocks
   * the fetch we fall back to pointing the frame straight at the signed URL —
   * still no download button, just a URL that's visible in the markup.
   */
  private async resolve(source: DocumentViewerSource): Promise<void> {
    this.release();
    const token = this.loadToken;

    if (source instanceof Blob) {
      this.show(this.retype(source));
      return;
    }

    if (typeof source !== 'string' || !source.trim()) {
      return;
    }

    this.resolving.set(true);
    this.failed.set(false);

    try {
      // HttpClient rather than fetch: it goes through the app's interceptors and
      // dev proxy, which is what makes the same request work elsewhere in the CRM.
      const blob = await firstValueFrom(
        this.http.get(source, { responseType: 'blob' as const }),
      );
      if (token !== this.loadToken) return;
      this.show(this.retype(blob));
    } catch {
      if (token !== this.loadToken) return;
      // Usually CORS on the storage account. The signed URL still renders in the
      // frame, it is just visible in the markup.
      this.directUrl.set(source);
    } finally {
      if (token === this.loadToken) {
        this.resolving.set(false);
      }
    }
  }

  private show(blob: Blob): void {
    if (this.kind() === 'pdf') {
      // Set before the URL so the iframe fallback never flashes up first.
      this.pdfState.set('loading');
      this.objectUrl.set(this.trackObjectUrl(blob));
      void this.loadPdf(blob);
      return;
    }

    this.objectUrl.set(this.trackObjectUrl(blob));
    if (this.kind() === 'image') {
      this.applyDefaultZoom();
    }
  }

  private async loadPdf(blob: Blob): Promise<void> {
    if (!this.isBrowser) {
      this.pdfState.set('failed');
      return;
    }

    const token = this.loadToken;

    try {
      // Imported on demand so pdf.js stays out of every app's initial bundle.
      const pdfjs = await import('pdfjs-dist');
      pdfjs.GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs/pdf.worker.min.mjs',
        document.baseURI,
      ).href;

      const data = new Uint8Array(await blob.arrayBuffer());
      const doc = await pdfjs.getDocument({ data, isEvalSupported: false }).promise;

      if (token !== this.loadToken) {
        void doc.destroy();
        return;
      }

      const sizes = await Promise.all(
        Array.from({ length: doc.numPages }, async (_, index) => {
          const page = await doc.getPage(index + 1);
          const viewport = page.getViewport({ scale: 1 });
          const baseScale = A4_WIDTH_PX / viewport.width;
          return {
            index,
            width: A4_WIDTH_PX,
            height: viewport.height * baseScale,
            baseScale,
          } satisfies PdfPageSize;
        }),
      );

      if (token !== this.loadToken) {
        void doc.destroy();
        return;
      }

      this.pdfDoc = doc;
      this.pages.set(sizes);
      this.currentPage.set(1);
      this.pdfState.set('ready');
      this.applyDefaultZoom();
    } catch {
      if (token === this.loadToken) {
        this.pdfState.set('failed');
      }
    }
  }

  /**
   * Only pages near the viewport hold a canvas. Off-screen ones are cleared, so
   * a 200-page PDF at 300% doesn't hold gigabytes of pixels.
   */
  private observePages(elements: HTMLElement[], root: HTMLElement | null): void {
    this.pageObserver?.disconnect();
    this.pageObserver = null;
    this.visiblePages.clear();

    if (!elements.length || !root || typeof IntersectionObserver === 'undefined') {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.pageObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const index = Number((entry.target as HTMLElement).dataset['page']);
            if (entry.isIntersecting) {
              this.visiblePages.add(index);
              void this.renderPage(index);
            } else {
              this.visiblePages.delete(index);
              this.clearPage(index, entry.target as HTMLElement);
            }
          }
        },
        { root, rootMargin: '100% 0px' },
      );

      elements.forEach((element) => this.pageObserver?.observe(element));
    });
  }

  /** Ctrl/Cmd + wheel (and trackpad pinch, which reports as ctrl+wheel) zooms. */
  private attachScroller(scroller: HTMLElement | null): void {
    this.detachScroller?.();
    this.detachScroller = null;

    if (!scroller) {
      return;
    }

    let frame = 0;

    const onWheel = (event: WheelEvent) => {
      if (!(event.ctrlKey || event.metaKey) || !this.canZoom()) {
        return;
      }

      event.preventDefault();
      // Clamped so one mouse-wheel notch is ~20%, while pinch deltas stay smooth.
      const delta = Math.max(-50, Math.min(50, event.deltaY));
      const next = this.zoom() * Math.exp(-delta * 0.004);
      this.zone.run(() => this.setZoom(next));
    };

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        this.updateCurrentPage(scroller);
      });
    };

    this.zone.runOutsideAngular(() => {
      scroller.addEventListener('wheel', onWheel, { passive: false });
      scroller.addEventListener('scroll', onScroll, { passive: true });
    });

    this.detachScroller = () => {
      scroller.removeEventListener('wheel', onWheel);
      scroller.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }

  private updateCurrentPage(scroller: HTMLElement): void {
    const elements = this.pageRefs();
    if (!elements.length) return;

    const middle = scroller.scrollTop + scroller.clientHeight / 2;
    let current = 1;
    for (const ref of elements) {
      const element = ref.nativeElement;
      if (element.offsetTop <= middle) {
        current = Number(element.dataset['page']) + 1;
      } else {
        break;
      }
    }

    if (current !== this.currentPage()) {
      this.currentPage.set(current);
    }
  }

  private scheduleRender(): void {
    if (this.renderTimer) {
      clearTimeout(this.renderTimer);
    }

    this.zone.runOutsideAngular(() => {
      this.renderTimer = setTimeout(() => {
        this.renderTimer = null;
        this.visiblePages.forEach((index) => void this.renderPage(index));
      }, 120);
    });
  }

  private async renderPage(index: number): Promise<void> {
    const doc = this.pdfDoc;
    const size = this.pages()[index];
    const element = this.pageRefs()[index]?.nativeElement;
    if (!doc || !size || !element) return;

    const token = this.loadToken;
    const zoom = this.zoom();
    const cssWidth = size.width * zoom;
    const cssHeight = size.height * zoom;

    const dpr = window.devicePixelRatio || 1;
    const outputScale = Math.min(dpr, Math.sqrt(MAX_CANVAS_PIXELS / (cssWidth * cssHeight)));
    const scale = size.baseScale * zoom * outputScale;

    if (this.renderedAt.get(index) === scale) return;
    this.renderTasks.get(index)?.cancel();
    this.renderedAt.set(index, scale);

    try {
      const page = await doc.getPage(index + 1);
      if (token !== this.loadToken) return;

      const viewport = page.getViewport({ scale });
      // Drawn off-DOM and swapped in, so a zoom never flashes a blank sheet.
      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      // Sized inline: a canvas made here never gets the _ngcontent attribute, so
      // the component styles can't reach it. Without this it renders at its
      // backing-store size — DPR × too big on 125%/150% scaled laptop screens.
      canvas.style.display = 'block';
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      const context = canvas.getContext('2d');
      if (!context) return;

      const task = page.render({ canvasContext: context, viewport });
      this.renderTasks.set(index, task);
      await task.promise;

      if (token !== this.loadToken || this.renderedAt.get(index) !== scale) return;
      this.renderTasks.delete(index);
      element.replaceChildren(canvas);
    } catch {
      // Cancelled by a newer zoom or by scrolling away — the newer render wins.
      if (this.renderedAt.get(index) === scale) {
        this.renderedAt.delete(index);
      }
    }
  }

  private clearPage(index: number, element: HTMLElement): void {
    this.renderTasks.get(index)?.cancel();
    this.renderTasks.delete(index);
    this.renderedAt.delete(index);
    element.replaceChildren();
  }

  /**
   * Blob storage often hands back `application/octet-stream`, which makes the
   * iframe offer a download instead of rendering. Re-tag it from the extension.
   */
  private retype(blob: Blob): Blob {
    if (blob.type && blob.type !== 'application/octet-stream') {
      return blob;
    }

    const kind = this.kind();
    if (kind === 'pdf') {
      return new Blob([blob], { type: 'application/pdf' });
    }

    if (kind === 'image') {
      const extension = this.extensionOf(this.fileName());
      const subtype = extension === 'jpg' ? 'jpeg' : extension === 'svg' ? 'svg+xml' : extension;
      return new Blob([blob], { type: `image/${subtype}` });
    }

    return blob;
  }

  private trackObjectUrl(blob: Blob): string {
    const url = URL.createObjectURL(blob);
    this.revokeHandle = url;
    return url;
  }

  private release(): void {
    this.loadToken++;

    if (this.renderTimer) {
      clearTimeout(this.renderTimer);
      this.renderTimer = null;
    }

    this.renderTasks.forEach((task) => task.cancel());
    this.renderTasks.clear();
    this.renderedAt.clear();
    this.visiblePages.clear();
    this.pageObserver?.disconnect();
    this.pageObserver = null;

    if (this.pdfDoc) {
      void this.pdfDoc.destroy();
      this.pdfDoc = null;
    }

    if (this.revokeHandle) {
      URL.revokeObjectURL(this.revokeHandle);
      this.revokeHandle = null;
    }

    this.objectUrl.set(null);
    this.directUrl.set(null);
    this.failed.set(false);
    this.resolving.set(false);
    this.pdfState.set('idle');
    this.pages.set([]);
    this.currentPage.set(1);
    this.zoom.set(1);
  }

  private extensionOf(fileName: string): string {
    const match = /\.([a-z0-9]+)\s*$/i.exec(fileName.trim());
    return match ? match[1].toLowerCase() : '';
  }
}
